/**
 * TowerScene — 無限魔王塔 endless mode (W4-TOWER)
 *
 * Architecture: Phaser scene that renders a top-down HUD overlay using Phaser
 * graphics/text.  Combat is abstracted — each floor has a boss + minions;
 * the player auto-attacks and the scene tracks HP / kills.  Actual sprite-level
 * rendering (entity movement, bullet physics) is deferred to a later milestone;
 * for now the scene exposes the full game-logic layer so the leaderboard, scaling
 * formulas, and run-recording are fully testable.
 *
 * Scaling formulas (N = floor number, 1-indexed):
 *   boss.hp   × 1.10^(N-1)
 *   enemy.hp  × 1.05^(N-1)
 *   atk cooldown × 0.97^(N-1)
 *
 * commanderXp reward per floor: floor × 10(玩遊戲軌;鐵則:絕不寫 freeXP/factionXP/
 *   skillLevels。2026-06-12 架構體檢已把本檔從誤用 addFreeXP 修正為 addCommanderXp,見下方 settle。)
 *
 * Score formula: floor*100 + kills*5 + max(0, 3600 - totalSec)
 */

import Phaser from 'phaser';
import { GAME, PLAYER } from '../config';
import { BOSS_POOL } from '../entities/boss-pool';
import { TIER2_BOSS_POOL } from '../entities/boss-tier2-pool';
import { BOSS_TIER3_POOL } from '../entities/boss-tier3-pool';
import type { BossDef } from '../entities/boss';
import { loadProgress, saveProgress, recordTowerRun } from '../../shared/progress';
import { addCommanderXp } from '../progression/commander';
import type { FactionId, TowerRun } from '../../shared/types';

// ── Scaling helpers (exported for tests) ────────────────────────────────────

export function scaledBossHp(baseHp: number, floor: number): number {
  return Math.round(baseHp * Math.pow(1.1, floor - 1));
}

export function scaledEnemyHp(baseHp: number, floor: number): number {
  return Math.round(baseHp * Math.pow(1.05, floor - 1));
}

export function scaledCooldownMs(baseCooldownMs: number, floor: number): number {
  return Math.round(baseCooldownMs * Math.pow(0.97, floor - 1));
}

export function computeScore(floor: number, kills: number, totalSec: number): number {
  return floor * 100 + kills * 5 + Math.max(0, 3600 - totalSec);
}

/**
 * Five-faction rotation for the tower. Cycles 5 floors per faction:
 *   F1-5 = celestial, 6-10 = runic, 11-15 = techno, 16-20 = elemental,
 *   21-25 = dominion, 26-30 = celestial …
 *
 * Boss floors (every 10th — F10, F20, F30, …) return `null` to signal
 * a tier-transition floor with no single dominant faction (boss draws
 * from its own tier pool instead of the rotation hint).
 *
 * Pure helper — no scene state, exported for unit tests.
 */
export function dominantFactionForFloor(floor: number): FactionId | null {
  if (floor <= 0) return null;
  if (floor % 10 === 0) return null;
  const cycle: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];
  return cycle[Math.floor((floor - 1) / 5) % cycle.length];
}

/**
 * Pick a boss for a given tower floor following the §4.2 tier table:
 *   F1-10   → BOSS_POOL (tier-1)
 *   F11-20  → TIER2_BOSS_POOL
 *   F21+    → BOSS_TIER3_POOL (cycles in upper floors)
 *
 * On floor 31+ the returned `BossDef` is shallow-cloned and the spawned
 * boss is flagged `_berserkMode = true` at spawn time (caller responsibility:
 * see TowerScene._initFloor).  Pure helper — exported for unit tests.
 *
 * Falls back to lower-tier pool if the target pool is empty (defensive —
 * shouldn't happen in production but keeps unit tests with stub pools green).
 */
export function pickTowerBoss(floor: number, rand: () => number = Math.random): BossDef {
  const tier1 = BOSS_POOL;
  const tier2 = TIER2_BOSS_POOL.length > 0 ? TIER2_BOSS_POOL : tier1;
  const tier3 = BOSS_TIER3_POOL.length > 0 ? BOSS_TIER3_POOL : tier2;

  let pool: BossDef[];
  if (floor <= 10) {
    pool = tier1;
  } else if (floor <= 20) {
    pool = tier2;
  } else {
    pool = tier3;
  }
  return pool[Math.floor(rand() * pool.length)];
}

/** True when a boss spawned on the given floor should start in berserk mode. */
export function shouldStartBerserk(floor: number): boolean {
  return floor >= 31;
}

// ── Internal state types ────────────────────────────────────────────────────

interface FloorState {
  floor: number;
  bossHp: number;
  bossMaxHp: number;
  bossAlive: boolean;
  minionsRemaining: number;
  minionsKilledThisFloor: number;
  /** Floor 31+ → boss spawns in berserk mode (mirrors `Boss._berserkMode`).
   *  When the renderer-bound Boss class is hooked up, this scene should set
   *  `boss['_berserkMode'] = true` at spawn — for now it's stored on the
   *  pure FloorState so unit tests can assert without a Phaser DOM. */
  berserkAtSpawn: boolean;
}

// ── Scene ───────────────────────────────────────────────────────────────────

export class TowerScene extends Phaser.Scene {
  // Run-level stats
  private currentFloor = 1;
  private totalKills = 0;
  private runStartMs = 0;

  // Per-floor state
  private floor!: FloorState;
  private playerHp: number = PLAYER.hp;
  private paused = false;
  private gameOver = false;

  // Auto-attack simulation
  private attackElapsedMs = 0;
  private attackCooldownMs: number = PLAYER.autoFireIntervalMs;

  // Random per run
  private rand: () => number = Math.random;

  // UI elements
  private floorText!: Phaser.GameObjects.Text;
  private hpText!: Phaser.GameObjects.Text;
  private killText!: Phaser.GameObjects.Text;
  private bossHpText!: Phaser.GameObjects.Text;
  private minionText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private continueBtn!: Phaser.GameObjects.Text;
  private continueHint!: Phaser.GameObjects.Text;

  // For testing: expose last recorded run
  lastRecordedRun: TowerRun | null = null;

  constructor() {
    super('TowerScene');
  }

  create(): void {
    this.currentFloor = 1;
    this.totalKills = 0;
    this.runStartMs = this.time.now;
    this.playerHp = PLAYER.hp;
    this.paused = false;
    this.gameOver = false;
    this.attackElapsedMs = 0;
    this.lastRecordedRun = null;

    this._initFloor(1);
    this._buildUI();
    this._refreshUI();
  }

  // ── Floor management ──────────────────────────────────────────────────────

  private _initFloor(floor: number): void {
    const bossDef = pickTowerBoss(floor, this.rand);
    const bossMaxHp = scaledBossHp(bossDef.hp, floor);
    const minionCount = floor + 2;

    this.floor = {
      floor,
      bossHp: bossMaxHp,
      bossMaxHp,
      bossAlive: true,
      minionsRemaining: minionCount,
      minionsKilledThisFloor: 0,
      berserkAtSpawn: shouldStartBerserk(floor),
    };
    this.attackCooldownMs = scaledCooldownMs(PLAYER.autoFireIntervalMs, floor);
    this.attackElapsedMs = 0;
    this.paused = false;
  }

  private _advanceToNextFloor(): void {
    // 過層獎勵 = 指揮官 XP(玩遊戲軌,帳號廣度)。鐵則:玩遊戲**絕不**發
    // freeXP/factionXP(freeXP 可被 allocateFreeXP 灌進戰力)— 本檔曾漏遷移
    // 用 addFreeXP,2026-06-12 架構體檢抓到後對齊 result-scene 的正確範本。
    const reward = this.currentFloor * 10;
    const progress = loadProgress();
    if (progress) {
      addCommanderXp(progress, reward);
      saveProgress(progress);
    }

    this.currentFloor += 1;
    this._initFloor(this.currentFloor);
    this._refreshUI();
  }

  // ── UI ─────────────────────────────────────────────────────────────────────

  private _buildUI(): void {
    const cx = GAME.width / 2;

    // Background
    this.add.rectangle(cx, GAME.height / 2, GAME.width, GAME.height, 0x0a0a1a);

    // Tower title
    this.add.text(cx, 24, '🗼 無限魔王塔', {
      fontSize: '28px', color: '#FFD700', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.floorText = this.add.text(cx, 60, '', {
      fontSize: '22px', color: '#E0E0E0', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.hpText = this.add.text(16, 16, '', {
      fontSize: '18px', color: '#4CAF50', fontFamily: 'sans-serif',
    });

    this.killText = this.add.text(16, 40, '', {
      fontSize: '16px', color: '#BDBDBD', fontFamily: 'sans-serif',
    });

    // Boss HP bar area
    this.bossHpText = this.add.text(cx, 100, '', {
      fontSize: '18px', color: '#EF5350', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.minionText = this.add.text(cx, 130, '', {
      fontSize: '16px', color: '#FF9800', fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.statusText = this.add.text(cx, GAME.height / 2, '', {
      fontSize: '24px', color: '#FFD54F', fontFamily: 'sans-serif',
      align: 'center',
    }).setOrigin(0.5);

    this.continueBtn = this.add.text(cx, GAME.height / 2 + 60, '[ 繼續爬塔 ]', {
      fontSize: '22px', color: '#00E5FF', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setVisible(false).setInteractive({ useHandCursor: true });

    this.continueBtn.on('pointerup', () => {
      if (this.paused && !this.gameOver) {
        this._advanceToNextFloor();
        this.continueBtn.setVisible(false);
        this.continueHint.setVisible(false);
        this.statusText.setText('');
      }
    });

    this.continueHint = this.add.text(cx, GAME.height / 2 + 100, '（或按 Space / Enter）', {
      fontSize: '14px', color: '#78909C', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setVisible(false);

    // Keyboard: Space / Enter advance floor; Escape = quit
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-SPACE', () => this._onContinueKey());
      this.input.keyboard.on('keydown-ENTER', () => this._onContinueKey());
      this.input.keyboard.on('keydown-ESC', () => this._quitRun());
    }
  }

  private _onContinueKey(): void {
    if (this.paused && !this.gameOver) {
      this._advanceToNextFloor();
      this.continueBtn.setVisible(false);
      this.continueHint.setVisible(false);
      this.statusText.setText('');
    }
  }

  private _refreshUI(): void {
    this.floorText.setText(`第 ${this.floor.floor} 層`);
    this.hpText.setText(`HP: ${this.playerHp} / ${PLAYER.hp}`);
    this.killText.setText(`總擊殺: ${this.totalKills}`);
    this.bossHpText.setText(`魔王 HP: ${this.floor.bossHp} / ${this.floor.bossMaxHp}`);
    this.minionText.setText(`小怪剩餘: ${this.floor.minionsRemaining}`);
  }

  // ── Game loop ──────────────────────────────────────────────────────────────

  update(_time: number, delta: number): void {
    if (this.paused || this.gameOver) return;

    this.attackElapsedMs += delta;
    if (this.attackElapsedMs >= this.attackCooldownMs) {
      this.attackElapsedMs = 0;
      this._simulateAttack();
    }

    // Enemies deal damage each second (simplified)
    const enemiesAlive = (this.floor.bossAlive ? 1 : 0) + this.floor.minionsRemaining;
    const dmgPerSec = enemiesAlive * 3; // 3 DPS per enemy unit
    const dmgThisTick = (dmgPerSec * delta) / 1000;
    this.playerHp = Math.max(0, this.playerHp - dmgThisTick);

    this._refreshUI();

    if (this.playerHp <= 0) {
      this._onPlayerDeath();
    } else if (!this.floor.bossAlive && this.floor.minionsRemaining === 0) {
      this._onFloorCleared();
    }
  }

  private _simulateAttack(): void {
    const dmg = PLAYER.damagePerHit;

    // Priority: kill minions first, then boss
    if (this.floor.minionsRemaining > 0) {
      // Each attack kills one minion (enemy HP much lower than attack)
      // More precise: track enemy HP per minion would need an array;
      // for tower mode simplification, each attack eliminates one minion.
      this.floor.minionsRemaining -= 1;
      this.floor.minionsKilledThisFloor += 1;
      this.totalKills += 1;
    } else if (this.floor.bossAlive) {
      this.floor.bossHp = Math.max(0, this.floor.bossHp - dmg);
      if (this.floor.bossHp === 0) {
        this.floor.bossAlive = false;
        this.totalKills += 1;
      }
    }
    // If both dead, _onFloorCleared fires in update loop
  }

  private _onFloorCleared(): void {
    this.paused = true;
    this.statusText.setText(`第 ${this.floor.floor} 層通關！\n+${this.floor.floor * 10} 修煉值`);
    this.continueBtn.setVisible(true);
    this.continueHint.setVisible(true);
  }

  private _onPlayerDeath(): void {
    this.playerHp = 0;
    this.gameOver = true;
    this._recordRun();
    this.statusText.setText(`第 ${this.currentFloor} 層陣亡\n3 秒後回到主頁…`);
    this.time.delayedCall(3000, () => {
      window.dispatchEvent(new CustomEvent('ef:return-home'));
    });
  }

  private _quitRun(): void {
    this.gameOver = true;
    this._recordRun();
    window.dispatchEvent(new CustomEvent('ef:return-home'));
  }

  private _recordRun(): void {
    const totalSec = Math.round((this.time.now - this.runStartMs) / 1000);
    const run: TowerRun = {
      highestFloor: this.currentFloor,
      totalSec,
      totalKills: this.totalKills,
      score: computeScore(this.currentFloor, this.totalKills, totalSec),
      endedAt: Date.now(),
    };
    this.lastRecordedRun = run;
    const progress = loadProgress();
    if (progress) {
      recordTowerRun(progress, run);
      saveProgress(progress);
    }
    // Emit event for leaderboard refresh
    window.dispatchEvent(new CustomEvent('ef:tower-run-ended', { detail: run }));
  }
}
